import { Flex } from "@chakra-ui/react";
import { formatNumber } from "accounting";
import { FC, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { Table } from "~/src/components/common/tables/Table";
import { CSVDownloadLink } from "~/src/components/features/report/CSVDownloadLink";
import {
  ReportTableDataRow,
  ReportTableHeaderRow,
} from "~/src/components/features/report/ReportTable";
import { ColumnData } from "~/src/components/features/report/ReportTable/ReportTableDataColumn";
import { headerOptionsForAdminSitePopupReport } from "~/src/components/features/report/utils";

export type AdminReportResultsProps = {
  startDate: string;
  endDate: string;
  siteIds: string[];
  orderValue: string;
  isDesc: boolean;
  onSortClick: (params: any) => void;
};

export const adminReportQuery = graphql`
  query AdminReportResults_Query(
    $filter: AdminPopupReportFilterInput!
    $order: PopupReportOrderInput
  ) {
    adminSitePopupReport(filter: $filter, order: $order) {
      rows {
        siteId
        siteName
        uniqueUser
        readyUser
        pageViewUser
        closeClick
        clickUser
        clickUserRate
        totalConversionUser
        totalConversionRate
      }
    }
  }
`;

export const AdminReportResults: FC<AdminReportResultsProps> = ({
  startDate,
  endDate,
  siteIds,
  orderValue,
  isDesc,
  onSortClick,
}) => {
  const variables: any = useMemo(
    () => ({
      filter: {
        startDate,
        endDate,
        siteIds,
      },
      order: {
        value: orderValue,
        isDesc,
      },
    }),
    [startDate, endDate, siteIds, orderValue, isDesc]
  );

  const data: any = useLazyLoadQuery(adminReportQuery, variables, {
    fetchPolicy: "store-and-network",
  });

  const rows: any[] = useMemo(
    () => data.adminSitePopupReport?.rows || [],
    [data]
  );

  const tableRows: ColumnData[][] = useMemo(() => {
    return rows.map((r) => [
      { type: "TEXT", value: String(r.siteName ?? "") },
      { type: "TEXT", value: String(r.siteId ?? "") },
      { type: "TEXT", value: formatNumber(r.uniqueUser || 0) },
      { type: "TEXT", value: formatNumber(r.readyUser || 0) },
      { type: "TEXT", value: formatNumber(r.pageViewUser || 0) },
      { type: "TEXT", value: formatNumber(r.closeClick || 0) },
      { type: "TEXT", value: formatNumber(r.clickUser || 0) },
      { type: "TEXT", value: `${r.clickUserRate ?? 0}%` },
      { type: "TEXT", value: formatNumber(r.totalConversionUser || 0) },
      { type: "TEXT", value: `${r.totalConversionRate ?? 0}%` },
    ]);
  }, [rows]);

  // テーブル下部のフッター行は不要のため表示しない

  const csvString = useMemo(() => {
    const headers = [
      "サイト名",
      "サイトID",
      "流入数",
      "Ready人数",
      "Imp数",
      "閉じる",
      "クリック人数",
      "CTR",
      "CV(計)",
      "CVR(計)",
    ];
    const dataRows = rows.map((r) => [
      r.siteName,
      r.siteId,
      r.uniqueUser,
      r.readyUser,
      r.pageViewUser,
      r.closeClick,
      r.clickUser,
      r.clickUserRate,
      r.totalConversionUser,
      r.totalConversionRate,
    ]);
    return [headers, ...dataRows]
      .map((cols) => cols.map((c) => String(c ?? "")).join(","))
      .join("\n");
  }, [rows]);

  return (
    <>
      <Flex justifyContent="flex-end" mb="24px" mt={6}>
        <CSVDownloadLink name="管理者用レポート" data={csvString} />
      </Flex>
      <Table>
        <ReportTableHeaderRow
          disableColumns={[]}
          onSortClick={onSortClick}
          headerOptions={headerOptionsForAdminSitePopupReport}
        />
        {tableRows.map((row, i) => (
          <ReportTableDataRow key={i} row={row} disableColumns={[]} />
        ))}
      </Table>
    </>
  );
};
